/**
 * @fileoverview File Your Appeal Activity Component
 * @module FileAppealActivity
 * 
 * Business Context:
 * - Guides users through the appeal submission process
 * - Provides form download link (DR-486 / 50-132)
 * - Explains Mail vs Electronic submission options
 * - Part of Step 1: File Your Appeal
 * 
 * Technical Context:
 * - Follows the activity pattern from Step 2/3 checklists
 * - Local state only (no backend persistence)
 * - Uses blue color scheme matching Step 1
 */

import React from 'react';
import {
  ArrowLeft,
  FileText,
  Mail,
  Monitor,
  CheckCircle2,
  Download,
  ExternalLink,
  Clock,
  AlertTriangle,
  Send
} from 'lucide-react';

interface FileAppealActivityProps {
  onComplete: () => void;
  onBack: () => void;
  isComplete: boolean;
}

const FileAppealActivity: React.FC<FileAppealActivityProps> = ({
  onComplete,
  onBack,
  isComplete
}) => {
  // Texas Comptroller DR-486 form (50-132)
  const FORM_URL = 'https://comptroller.texas.gov/forms/50-132.pdf';

  return (
    <div className="bg-white rounded-2xl shadow-xl border border-gray-100">
      {/* Header */}
      <div className="p-6 border-b border-gray-100">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-gray-600 hover:text-blue-600 mb-4 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to videos
        </button>
        <div className="flex items-center gap-3">
          <div className="p-3 bg-blue-50 rounded-lg text-blue-600">
            <Send className="w-6 h-6" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Submit Your Appeal</h2>
            <p className="text-gray-600">Choose how to file your property tax protest</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6 space-y-6">
        {/* Form Download Section */}
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100">
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <FileText className="w-7 h-7 text-blue-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-bold text-gray-900 mb-2">Property Tax Protest Form (50-132)</h3>
              <p className="text-gray-700 mb-4">
                This is the official Texas form to protest your property's appraised value. 
                You'll use this whether you submit by mail or online.
              </p>
              <a
                href={FORM_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-5 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors"
              >
                <Download className="w-5 h-5" />
                Download Form (PDF)
                <ExternalLink className="w-4 h-4 ml-1" />
              </a>
            </div>
          </div>
        </div>

        {/* Submission Methods */}
        <div>
          <h3 className="text-lg font-bold text-gray-900 mb-4">How to Submit Your Appeal</h3>
          <div className="grid md:grid-cols-2 gap-4">
            {/* Mail Option */}
            <div className="bg-gray-50 rounded-xl p-5 border-2 border-gray-200 hover:border-blue-200 transition-colors">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Mail className="w-6 h-6 text-blue-600" />
                </div>
                <h4 className="text-lg font-bold text-gray-900">By Mail</h4>
              </div>
              <ol className="space-y-3 text-gray-600">
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center text-blue-700 font-bold text-sm flex-shrink-0">1</span>
                  <span>Print and complete the 50-132 form</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center text-blue-700 font-bold text-sm flex-shrink-0">2</span>
                  <span>Sign with your original signature</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center text-blue-700 font-bold text-sm flex-shrink-0">3</span>
                  <span>Mail to your County Appraisal District (CAD)</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center text-blue-700 font-bold text-sm flex-shrink-0">4</span>
                  <span>Use certified mail for proof of delivery</span>
                </li>
              </ol>
              <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
                <p className="text-blue-800 text-sm">
                  <strong>Tip:</strong> Find your CAD's mailing address on your appraisal notice or search "[Your County] Appraisal District" online.
                </p>
              </div>
            </div>

            {/* Electronic Option */}
            <div className="bg-gray-50 rounded-xl p-5 border-2 border-gray-200 hover:border-blue-200 transition-colors">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Monitor className="w-6 h-6 text-blue-600" />
                </div>
                <h4 className="text-lg font-bold text-gray-900">Electronic (Portal)</h4>
              </div>
              <ol className="space-y-3 text-gray-600">
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center text-blue-700 font-bold text-sm flex-shrink-0">1</span>
                  <span>Visit your CAD's website</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center text-blue-700 font-bold text-sm flex-shrink-0">2</span>
                  <span>Create an account or log in</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center text-blue-700 font-bold text-sm flex-shrink-0">3</span>
                  <span>Find "File a Protest" or similar option</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center text-blue-700 font-bold text-sm flex-shrink-0">4</span>
                  <span>Complete the online form and submit</span>
                </li>
              </ol>
              <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
                <p className="text-blue-800 text-sm">
                  <strong>Tip:</strong> Electronic filing gives you instant confirmation. Save or screenshot your confirmation number!
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Important Deadlines */}
        <div className="bg-amber-50 rounded-xl p-5 border border-amber-200">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-bold text-amber-900 mb-2">Important Deadlines</h4>
              <ul className="space-y-2 text-amber-800">
                <li className="flex items-start gap-2">
                  <Clock className="w-4 h-4 flex-shrink-0 mt-1" />
                  <span><strong>May 15th</strong> (or 30 days from your notice date, whichever is later) is the typical deadline to file your protest.</span>
                </li>
                <li className="flex items-start gap-2">
                  <Clock className="w-4 h-4 flex-shrink-0 mt-1" />
                  <span>Check your appraisal notice for your specific deadline date.</span>
                </li>
                <li className="flex items-start gap-2">
                  <Clock className="w-4 h-4 flex-shrink-0 mt-1" />
                  <span>File as soon as possible - don't wait until the last day!</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* What's on the Form */}
        <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
          <h4 className="font-bold text-gray-900 mb-3">What You'll Need to Complete the Form</h4>
          <ul className="space-y-2 text-gray-700">
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
              <span>Your property's account number (from appraisal notice)</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
              <span>Your property address</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
              <span>The reason for your protest (check "Value is over market value" at minimum)</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
              <span>Your signature and date</span>
            </li>
          </ul>
          <p className="mt-4 text-gray-600 text-sm">
            <strong>Note:</strong> Watch the videos in this step to learn exactly how to fill out each section of the form.
          </p>
        </div>
      </div>

      {/* Mark Complete Button */}
      <div className="p-6 border-t border-gray-100 bg-gray-50 rounded-b-2xl">
        {isComplete ? (
          <div className="flex items-center justify-center gap-2 py-3 px-6 bg-green-100 text-green-700 rounded-lg font-semibold">
            <CheckCircle2 className="w-5 h-5" />
            Completed
          </div>
        ) : (
          <button
            onClick={onComplete}
            className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
          >
            <CheckCircle2 className="w-5 h-5" />
            Mark as Complete
          </button>
        )}
      </div>
    </div>
  );
};

export default FileAppealActivity;







